import { ReactElement, useState, useEffect, useCallback } from 'react'
import { ContextProps, createContext } from '../common/contextHelper'
import { useAuthContext } from './AuthContext'
import { useWindowDimensions } from '../hooks/useWindowDimensions'
import { SensorProduct } from '../common/enums'
import { SensorSubType } from '../components/SensorTypeSelector'
import { MAX_COMPARE_DEVICES } from '../common/constants'

// Compare Mode Types
export interface CompareSelection {
  deviceId: string       // sigfoxId
  // Selection tokens for this device: a port ("A1", "B2"), a virtual
  // device-level port ("T0", "BATT"), or — in mixed mode, for rainfall/flow
  // ports that expose several metrics — "port~metric" ("A2~rate").
  // One token = one chart series. See utils/portTokens.
  sensorPorts: string[]
}

// Single = today's flow (many devices, one sensor type, one axis).
// Mixed  = overlay different sensor types on one chart (auto-axis/normalized).
export type CompareVariant = 'single' | 'mixed'

// A (device, port) pair used for bulk selection in mixed mode.
export interface SensorTuple {
  deviceId: string
  sensorPort: string
}

interface AppContextState {
  showLogo: boolean
  showDeviceListDrawer: boolean
  showCharts: boolean
  chartsToShow: string[]
  currentDate?: Date
  showSidebarNav: boolean
  showBatteryCharts: boolean
  showTemperatureCharts: boolean
  // Compare Mode State
  compareMode: boolean
  compareVariant: CompareVariant          // 'single' (one sensor type) vs 'mixed' (overlay types)
  selectedDevicesForComparison: string[]  // sigfoxIds (device selection step)
  selectedSensorType: string | null       // e.g., "Irrometer", "Humidity" (single mode only)
  selectedSubType: SensorSubType | null   // e.g., "rainfallVolume", "flowRate" for sensors with sub-types
  selectedSensors: CompareSelection[]     // granular: which specific sensors per device
  setShowDeviceListDrawer: (show: boolean) => void
  toggleChartsToShow: () => void
  setCurrentDate: (date: Date) => void
  setShowCharts: (show: boolean) => void
  setShowSidebarNav: (show: boolean) => void
  setShowBatteryCharts: (show: boolean) => void
  setShowTemperatureCharts: (show: boolean) => void
  // Compare Mode Actions
  setCompareMode: (enabled: boolean) => void
  setCompareVariant: (variant: CompareVariant) => void
  toggleSensorsBulk: (tuples: SensorTuple[]) => void
  toggleDeviceForComparison: (sigfoxId: string) => void
  setDevicesForComparison: (sigfoxIds: string[]) => void
  setSelectedSensorType: (sensorType: string | null) => void
  setSelectedSubType: (subType: SensorSubType | null) => void
  setSelectedSensors: (sensors: CompareSelection[]) => void
  toggleSensorForDevice: (deviceId: string, sensorPort: string) => void
  toggleSensorWithType: (deviceId: string, sensorPort: string, sensorType: string) => void
  selectAllSensorsForType: (sensorType: string, deviceSensors: Map<string, string[]>) => void
  selectSensorsByPort: (port: string, deviceSensors: Map<string, string[]>) => void
  selectSensorsByDepth: (depth: number, deviceSensors: Map<string, { port: string; depth: number }[]>) => void
  switchSensorType: (newSensorType: string, deviceSensorMap: Map<string, string>) => void
  clearSensorSelections: () => void
  clearComparison: () => void
}

const [Provider, useAppContext] = createContext<AppContextState>(module.filename)

const SMALL_SCREEN_HEIGHT = 900

export { useAppContext }

const PRIMARY_CHART_LIST = ['A1', 'A2', 'B1', 'B2', 'C1', 'C2', 'C3']
const SECONDARY_CHART_LIST = [
  // 'Temperature',
  SensorProduct.Battery,
]

// Mixed-mode overlays different sensor types on one chart; cap total series so
// the 10-color palette stays a reliable identifier and the fan-out stays bounded.
// Single-type mode is uncapped in series (it can legitimately show many same-unit
// lines) and is bounded instead by the device cap above.
export const MAX_COMPARE_SERIES = 10

export function AppProvider({ children }: ContextProps): ReactElement {
  const { height } = useWindowDimensions()

  const { isAuthorized } = useAuthContext()

  const [showDeviceListDrawer, setShowDeviceListDrawer] = useState(false)
  const [showCharts, setShowCharts] = useState(false)
  const [chartsToShow, setChartsToShow] = useState(PRIMARY_CHART_LIST)
  const [currentDate, setCurrentDate] = useState<Date>()
  const [showSidebarNav, setShowSidebarNav] = useState(false)
  const [showBatteryCharts, setShowBatteryCharts] = useState(false)
  const [showTemperatureCharts, setShowTemperatureCharts] = useState(true)

  // Compare Mode State
  const [compareMode, setCompareModeInternal] = useState(false)
  const [compareVariant, setCompareVariantInternal] = useState<CompareVariant>('single')
  const [selectedDevicesForComparison, setSelectedDevicesForComparison] = useState<string[]>([])
  const [selectedSensorType, setSelectedSensorType] = useState<string | null>(null)
  const [selectedSubType, setSelectedSubType] = useState<SensorSubType | null>(null)
  const [selectedSensors, setSelectedSensors] = useState<CompareSelection[]>([])

  const showLogo = height > SMALL_SCREEN_HEIGHT || !isAuthorized

  useEffect(() => {
    if (showSidebarNav) {
      setShowDeviceListDrawer(false)
    }
  }, [showSidebarNav])

  useEffect(() => {
    if (showDeviceListDrawer) {
      setShowSidebarNav(false)
    }
  }, [showDeviceListDrawer])

  function toggleChartsToShow() {
    setChartsToShow(chartsToShow === PRIMARY_CHART_LIST ? SECONDARY_CHART_LIST : PRIMARY_CHART_LIST)
  }

  // Compare Mode Actions
  const setCompareMode = useCallback((enabled: boolean) => {
    setCompareModeInternal(enabled)
    if (!enabled) {
      // Clear all compare state when exiting
      setCompareVariantInternal('single')
      setSelectedDevicesForComparison([])
      setSelectedSensorType(null)
      setSelectedSubType(null)
      setSelectedSensors([])
    }
  }, [])

  // Switching variant clears the current selection: single mode keys everything
  // off selectedSensorType (null in mixed), and mixed selections may span types
  // that single mode can't render together, so a clean slate avoids stale state.
  // The clear is done as plain enqueued updates (not inside the setState updater)
  // so that a following setSelectedSensors(...) — e.g. restoring a shared
  // &mode=mixed URL — is applied after the clear and wins.
  const setCompareVariant = useCallback((variant: CompareVariant) => {
    if (variant !== compareVariant) {
      setSelectedSensorType(null)
      setSelectedSubType(null)
      setSelectedSensors([])
    }
    setCompareVariantInternal(variant)
  }, [compareVariant])

  // Bulk toggle for mixed-mode "add all of type X" pills. If every tuple is
  // already selected, remove them all; otherwise add the missing ones.
  const toggleSensorsBulk = useCallback((tuples: SensorTuple[]) => {
    if (tuples.length === 0) return
    setSelectedSensors(prev => {
      const isSelected = (deviceId: string, port: string) =>
        prev.find(s => s.deviceId === deviceId)?.sensorPorts.includes(port) ?? false
      const allSelected = tuples.every(t => isSelected(t.deviceId, t.sensorPort))

      // Work on a mutable map of deviceId -> Set<port>
      const map = new Map<string, Set<string>>()
      for (const s of prev) map.set(s.deviceId, new Set(s.sensorPorts))
      let total = prev.reduce((sum, s) => sum + s.sensorPorts.length, 0)

      for (const { deviceId, sensorPort } of tuples) {
        const ports = map.get(deviceId) ?? new Set<string>()
        if (allSelected) {
          if (ports.delete(sensorPort)) total--
        } else if (!ports.has(sensorPort)) {
          // Respect the mixed-mode series cap; add only up to the limit.
          if (compareVariant === 'mixed' && total >= MAX_COMPARE_SERIES) continue
          ports.add(sensorPort)
          total++
        }
        map.set(deviceId, ports)
      }

      const next: CompareSelection[] = []
      map.forEach((ports, deviceId) => {
        if (ports.size > 0) next.push({ deviceId, sensorPorts: Array.from(ports) })
      })
      return next
    })
  }, [compareVariant])

  const toggleDeviceForComparison = useCallback((sigfoxId: string) => {
    // Check if device is currently selected
    const isCurrentlySelected = selectedDevicesForComparison.includes(sigfoxId)

    if (isCurrentlySelected) {
      // Remove device
      setSelectedDevicesForComparison(prev => prev.filter(id => id !== sigfoxId))
      // Also remove from selected sensors
      setSelectedSensors(sensors => sensors.filter(s => s.deviceId !== sigfoxId))
    } else if (selectedDevicesForComparison.length < MAX_COMPARE_DEVICES) {
      // Add device
      setSelectedDevicesForComparison(prev => [...prev, sigfoxId])
    }
    // At limit, don't add
  }, [selectedDevicesForComparison])

  // Idempotently set the compared-device list (deduped, capped). Unlike
  // toggleDeviceForComparison this replaces rather than appends, so it is safe
  // to call repeatedly — e.g. restoring from the URL across multiple mounted
  // hook instances — without accumulating duplicates.
  const setDevicesForComparison = useCallback((sigfoxIds: string[]) => {
    const deduped = Array.from(new Set(sigfoxIds)).slice(0, MAX_COMPARE_DEVICES)
    setSelectedDevicesForComparison(prev => {
      if (prev.length === deduped.length && prev.every((id, i) => id === deduped[i])) {
        return prev
      }
      return deduped
    })
    // Drop any sensor selections for devices no longer in the list.
    setSelectedSensors(sensors => sensors.filter(s => deduped.includes(s.deviceId)))
  }, [])

  const toggleSensorForDevice = useCallback((deviceId: string, sensorPort: string) => {
    setSelectedSensors(prev => {
      const existing = prev.find(s => s.deviceId === deviceId)
      const alreadySelected = existing?.sensorPorts.includes(sensorPort) ?? false

      // Enforce the mixed-mode series cap on add (removals are always allowed).
      if (!alreadySelected && compareVariant === 'mixed') {
        const total = prev.reduce((sum, s) => sum + s.sensorPorts.length, 0)
        if (total >= MAX_COMPARE_SERIES) return prev
      }

      if (existing) {
        const hasPort = existing.sensorPorts.includes(sensorPort)
        if (hasPort) {
          // Remove port
          const newPorts = existing.sensorPorts.filter(p => p !== sensorPort)
          if (newPorts.length === 0) {
            // Remove entire device entry if no ports left
            return prev.filter(s => s.deviceId !== deviceId)
          }
          return prev.map(s => s.deviceId === deviceId ? { ...s, sensorPorts: newPorts } : s)
        } else {
          // Add port
          return prev.map(s => s.deviceId === deviceId
            ? { ...s, sensorPorts: [...s.sensorPorts, sensorPort] }
            : s
          )
        }
      } else {
        // Create new entry for device
        return [...prev, { deviceId, sensorPorts: [sensorPort] }]
      }
    })
  }, [compareVariant])

  // Toggle sensor with automatic sensor type management
  // First click sets sensor type, subsequent clicks toggle sensors of same type
  // Clearing all sensors clears the sensor type
  const toggleSensorWithType = useCallback((deviceId: string, sensorPort: string, sensorType: string) => {
    // Calculate what the new selections will be
    const existing = selectedSensors.find(s => s.deviceId === deviceId)
    let newSelections: CompareSelection[]

    if (existing) {
      const hasPort = existing.sensorPorts.includes(sensorPort)
      if (hasPort) {
        // Remove port
        const newPorts = existing.sensorPorts.filter(p => p !== sensorPort)
        if (newPorts.length === 0) {
          newSelections = selectedSensors.filter(s => s.deviceId !== deviceId)
        } else {
          newSelections = selectedSensors.map(s => s.deviceId === deviceId ? { ...s, sensorPorts: newPorts } : s)
        }
      } else {
        // Add port
        newSelections = selectedSensors.map(s => s.deviceId === deviceId
          ? { ...s, sensorPorts: [...s.sensorPorts, sensorPort] }
          : s
        )
      }
    } else {
      // Create new entry for device
      newSelections = [...selectedSensors, { deviceId, sensorPorts: [sensorPort] }]
    }

    // Check if all sensors cleared
    const totalSelected = newSelections.reduce((sum, s) => sum + s.sensorPorts.length, 0)

    // Set sensor type if adding first sensor, clear if removing last
    if (selectedSensorType === null && totalSelected > 0) {
      setSelectedSensorType(sensorType)
    } else if (totalSelected === 0) {
      setSelectedSensorType(null)
    }

    // Update selections
    setSelectedSensors(newSelections)
  }, [selectedSensors, selectedSensorType])

  const selectAllSensorsForType = useCallback((sensorType: string, deviceSensors: Map<string, string[]>) => {
    const newSelections: CompareSelection[] = []
    deviceSensors.forEach((ports, deviceId) => {
      newSelections.push({ deviceId, sensorPorts: ports })
    })
    setSelectedSensors(newSelections)
  }, [])

  const selectSensorsByPort = useCallback((port: string, deviceSensors: Map<string, string[]>) => {
    const newSelections: CompareSelection[] = []
    deviceSensors.forEach((ports, deviceId) => {
      const matchingPorts = ports.filter(p => p === port)
      if (matchingPorts.length > 0) {
        newSelections.push({ deviceId, sensorPorts: matchingPorts })
      }
    })
    setSelectedSensors(newSelections)
  }, [])

  const selectSensorsByDepth = useCallback((depth: number, deviceSensors: Map<string, { port: string; depth: number }[]>) => {
    const newSelections: CompareSelection[] = []
    deviceSensors.forEach((sensors, deviceId) => {
      const matchingPorts = sensors.filter(s => s.depth === depth).map(s => s.port)
      if (matchingPorts.length > 0) {
        newSelections.push({ deviceId, sensorPorts: matchingPorts })
      }
    })
    setSelectedSensors(newSelections)
  }, [])

  // Atomic sensor type switch - handles clearing and re-selecting in a single state update
  // This avoids race conditions from separate onClearAll + onSelectSensorType + onToggleSensor calls
  const switchSensorType = useCallback((newSensorType: string, deviceSensorMap: Map<string, string>) => {
    // Build new selections atomically
    const newSelections: CompareSelection[] = []
    deviceSensorMap.forEach((port, deviceId) => {
      newSelections.push({ deviceId, sensorPorts: [port] })
    })

    // Set both at once - React will batch these together
    setSelectedSensorType(newSensorType)
    setSelectedSensors(newSelections)
  }, [])

  // Clear sensor selections but keep devices selected
  const clearSensorSelections = useCallback(() => {
    setSelectedSensorType(null)
    setSelectedSubType(null)
    setSelectedSensors([])
  }, [])

  const clearComparison = useCallback(() => {
    setCompareVariantInternal('single')
    setSelectedDevicesForComparison([])
    setSelectedSensorType(null)
    setSelectedSubType(null)
    setSelectedSensors([])
    setCompareModeInternal(false)
  }, [])

  return (
    <Provider
      value={{
        showLogo,
        showDeviceListDrawer,
        chartsToShow,
        currentDate,
        showCharts,
        showSidebarNav,
        showBatteryCharts,
        showTemperatureCharts,
        // Compare Mode State
        compareMode,
        compareVariant,
        selectedDevicesForComparison,
        selectedSensorType,
        selectedSubType,
        selectedSensors,
        setShowDeviceListDrawer,
        toggleChartsToShow,
        setCurrentDate,
        setShowCharts,
        setShowSidebarNav,
        setShowBatteryCharts,
        setShowTemperatureCharts,
        // Compare Mode Actions
        setCompareMode,
        setCompareVariant,
        toggleSensorsBulk,
        toggleDeviceForComparison,
        setDevicesForComparison,
        setSelectedSensorType,
        setSelectedSubType,
        setSelectedSensors,
        toggleSensorForDevice,
        toggleSensorWithType,
        selectAllSensorsForType,
        selectSensorsByPort,
        selectSensorsByDepth,
        switchSensorType,
        clearSensorSelections,
        clearComparison,
      }}
    >
      {children}
    </Provider>
  )
}
